import { useQuery } from 'react-query';
import axios from 'axios';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoTexto from '@/components/Formulario/CampoTexto';
import Grid, { GridItem } from '@/components/layout/Grid';

import { Accordion } from '@/components/layout/Accordion';
import type {
  NetworkLanDeviceDTO,
  NetworkLanDeviceTypeDTO,
  NetworkWanDTO,
} from '@webip/shared/src/dto/appliance';
import CampoMemo from '@/components/Formulario/CampoMemo';
import CampoMultiSeletor from '@/components/Formulario/CampoMultiSeletor';
import { IconeTipoDispositivoLan } from '@/components/IconeTipoDispositivoLan';
import HBox from '@/components/layout/HBox';

export const FormLanDevice = ({
  value,
  onChange,
  fieldErrors,
  grupoIds = [],
  onGrupoIdsChange,
  grupoOptions = [],
}: {
  value: NetworkLanDeviceDTO;
  onChange: (v: NetworkLanDeviceDTO) => void;
  fieldErrors: Record<string, string>;
  grupoIds?: number[];
  onGrupoIdsChange?: (ids: number[]) => void;
  grupoOptions?: { id: string; label: string }[];
}) => {
  const applianceId = value?.appliance_id;
  const { data: wans } = useQuery(
    ['redes-wan', applianceId],
    async () => {
      const res = await axios.get<{ result: NetworkWanDTO[] }>('/api/rede_wan', {
        params: { fi: JSON.stringify({ appliance_id: ['eq', applianceId] }), t: 100 },
      });
      return res.data.result;
    },
    { enabled: !!applianceId }
  );

  // Afinidade só faz sentido com mais de uma WAN (primária ou backup — com uma
  // primária só, fixar num backup tira o dispositivo da main). Uma afinidade já
  // gravada continua visível (e removível) mesmo se a segunda WAN sumiu.
  const afinidadeAtual = value?.wan_afinidade_id ?? null;
  const multiWan = (wans ?? []).length > 1;
  const wanOptions = (wans ?? [])
    .map(w => ({
      id: String(w.id),
      label: w.uso === 'primario' ? w.nome : `${w.nome} (backup)`,
    }));

  return (
    <Grid>
      <div style={{ position: 'absolute', height: 0 }}>
        <input
          type="text"
          name="username"
          style={{
            height: 0,
            width: 0,
            border: 'none',
            outline: 'none',
            margin: 0,
            padding: 0,
          }}
        />
        <input
          type="password"
          name="password"
          style={{
            height: 0,
            width: 0,
            border: 'none',
            outline: 'none',
            margin: 0,
            padding: 0,
          }}
        />
      </div>
      <GridItem sm={12}>
        <Accordion title="Geral" startOpen>
          <Grid>
            <GridItem sm={12} md={4}>
              <CampoSeletor
                noSearch
                label="Ícone"
                value={value?.tipo}
                onChange={v =>
                  onChange({ ...value, tipo: v as NetworkLanDeviceTypeDTO })
                }
                options={[
                  { id: 'pc', label: <HBox gap={2}><IconeTipoDispositivoLan tipo="pc" /> PC</HBox> },
                  { id: 'router', label: <HBox gap={2}><IconeTipoDispositivoLan tipo="router" /> Router</HBox> },
                  { id: 'servidor', label: <HBox gap={2}><IconeTipoDispositivoLan tipo="servidor" /> Servidor</HBox> },
                  { id: 'impressora', label: <HBox gap={2}><IconeTipoDispositivoLan tipo="impressora" /> Impressora</HBox> },
                  { id: 'tv', label: <HBox gap={2}><IconeTipoDispositivoLan tipo="tv" /> TV</HBox> },
                  { id: 'iot', label: <HBox gap={2}><IconeTipoDispositivoLan tipo="iot" /> IoT</HBox> },
                  { id: 'outro', label: <HBox gap={2}><IconeTipoDispositivoLan tipo="outro" /> Outro</HBox> },
                ]}
                error={fieldErrors?.tipoLan}
              />
            </GridItem>
            <GridItem sm={12} md={8}>
              <CampoTexto
                value={value?.nome ?? ''}
                onChange={v => onChange({ ...value, nome: v })}
                label="Nome"
                error={fieldErrors?.nome}
              />
            </GridItem>

            <GridItem sm={12} md={6}>
              <CampoTexto
                value={value?.ip}
                onChange={v =>
                  onChange({
                    ...value,
                    ip: v,
                  })
                }
                label="IP"
                error={
                  fieldErrors &&
                  fieldErrors[
                  Object.keys(fieldErrors).find(k => k.startsWith('ip'))!
                  ]
                }
                placeholder="Ex: 192.168.88.10"
              />
            </GridItem>
            <GridItem sm={12} md={6}>
              <CampoTexto
                value={value?.mac ?? ''}
                onChange={v =>
                  onChange({
                    ...value,
                    mac: v,
                  })
                }
                label="MAC"
                error={
                  fieldErrors &&
                  fieldErrors[
                  Object.keys(fieldErrors).find(k => k.startsWith('mac'))!
                  ]
                }
                placeholder="Formato: 00:11:22:33:44:55"
              />
            </GridItem>
            <GridItem sm={12}>
              <CampoSeletor
                noSearch
                nullable
                label="Afinidade de WAN"
                value={afinidadeAtual != null ? String(afinidadeAtual) : null}
                onChange={v =>
                  onChange({ ...value, wan_afinidade_id: v ? +v : null })
                }
                options={wanOptions}
                disabled={!multiWan && afinidadeAtual == null}
                placeholder={multiWan ? 'Sem afinidade (padrão)' : 'Requer mais de uma WAN'}
                hint="Fixa a saída deste dispositivo no link escolhido (com failover para os demais) em vez do balanceamento."
                error={fieldErrors?.wan_afinidade_id}
              />
            </GridItem>
          </Grid>
        </Accordion>
      </GridItem>

      <GridItem sm={12}>
        <CampoMemo
          label="Notas e observações"
          value={value?.obs ?? ''}
          onChange={v => onChange({ ...value, obs: v })}
          error={fieldErrors?.obs}
          placeholder="Notas sobre o dispositivo"
        />
      </GridItem>

      {onGrupoIdsChange && (
        <GridItem sm={12}>
          <CampoMultiSeletor
            label="Grupos de dispositivos"
            value={grupoIds.map(String)}
            onChange={ids => onGrupoIdsChange(ids.map(Number))}
            options={grupoOptions}
            placeholder="Selecione os grupos"
          />
        </GridItem>
      )}
    </Grid>
  );
};
